import {
  Lci3Report,
  Subdimension,
} from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { Card, Stack, Typography } from '@mui/material';
import { SyntheticEvent, useState } from 'react';
import { optimizationIndexOptions } from './DimensionsDataGrid/columns';
import { DimensionsDataGrid } from './DimensionsDataGrid';
import { ALL_OPTION } from '../../../../../components/RealTimeOptimization/DimensionTabs';
import { Highlights } from './Highlights';
import { ColumnTypeSelect, ColumnTypeValue } from './ColumnTypeSelect';
import { Option } from './DimensionsDataGrid/columns';
import { DimensionFilter } from '@features/campaigns/components/RealTimeOptimization/ComparisonByDimension/DimensionFilter';
import { buildDimensionCounts } from '@features/reports/subdomains/visitation';
import { AIInsightsCard } from '@components/shared/AIInsightsCard';
import { useLciAIInsights, MetricsType } from '@features/ai-insights';
import { apiSlugToDimensionName } from '@components/Dashboard/dashboardSlice/responseMappers/dimensionStats';

const getDefaultOptimizationOption = (
  props: Pick<
    ComparisonByDimensionCardProps,
    'selectedReport' | 'latestSalesReport'
  >
): Option => {
  if (props.latestSalesReport?.salesMetrics) {
    return (props.latestSalesReport.salesMetrics.incrementalSales.total ?? 0) >
      0
      ? optimizationIndexOptions.incrementalSalesPerImpressionIndex
      : optimizationIndexOptions.totalSalesPerImpressionIndex;
  }

  return (props.selectedReport.visitMetrics.incrementalVisits ?? 0) > 0
    ? optimizationIndexOptions.incrementalVisitsPerImpressionIndex
    : optimizationIndexOptions.totalVisitsPerImpressionIndex;
};

type ComparisonByDimensionCardProps = {
  selectedReport: Lci3Report;
  latestSalesReport?: Lci3Report;
  campaignName: string;
  subdimensions: Subdimension[];
};

const isDimensionSlug = (
  value: string
): value is keyof typeof apiSlugToDimensionName => {
  return value in apiSlugToDimensionName;
};

const resolveMetricsType = (
  hasRSA: boolean,
  selectedColumnType: ColumnTypeValue
): MetricsType => {
  if (!hasRSA) return 'summary';
  return selectedColumnType === 'keyMetrics'
    ? 'key-metrics'
    : 'contribution-metrics';
};

export const ComparisonByDimensionCard = (
  props: ComparisonByDimensionCardProps
) => {
  const [activeTab, setActiveTab] = useState<string>(ALL_OPTION.value);
  const [selectedOptimization, setSelectedOptimization] = useState<
    Option['value']
  >(
    getDefaultOptimizationOption({
      selectedReport: props.selectedReport,
      latestSalesReport: props.latestSalesReport,
    }).value
  );
  const [selectedColumnType, setSelectedColumnType] =
    useState<ColumnTypeValue>('keyMetrics');
  const handleTabChange = (_: SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };
  const dimensionCounts = buildDimensionCounts(props.subdimensions);

  const showSalesColumns = props.subdimensions.some(
    subdimension => subdimension.salesMetrics
  );

  // TODO: refactor
  const hasRSA = !!props.latestSalesReport?.salesMetrics;
  const metricsType = resolveMetricsType(hasRSA, selectedColumnType);
  // end TODO

  const { rsaAiInsights } = useLciAIInsights(
    props.latestSalesReport ?? null,
    metricsType,
    activeTab
  );
  const { footTrafficAiInsights } = useLciAIInsights(
    props.selectedReport,
    'summary',
    activeTab
  );

  const aiInsightsTitle = isDimensionSlug(activeTab)
    ? apiSlugToDimensionName[activeTab]
    : 'All Dimensions';

  return (
    <Card sx={{ padding: '1rem' }}>
      <Stack direction="column" gap="1rem">
        <Typography variant="h5">Comparison by Dimension</Typography>
        {showSalesColumns ? (
          <ColumnTypeSelect
            value={selectedColumnType}
            onChange={setSelectedColumnType}
          />
        ) : null}
        <DimensionFilter
          activeTab={activeTab}
          onChange={handleTabChange}
          allSubdimensionsCount={props.subdimensions.length}
          dimensionCounts={dimensionCounts}
        />

        <AIInsightsCard
          testId="comparison-by-dimension-ai-insights"
          aiInsights={rsaAiInsights ?? footTrafficAiInsights ?? undefined}
          title={aiInsightsTitle}
          highlights={
            <Highlights
              campaignName={props.campaignName}
              selectedReport={props.selectedReport}
              salesMetrics={props.latestSalesReport?.salesMetrics ?? null}
            />
          }
        />

        <DimensionsDataGrid
          filterByDimension={activeTab}
          subdimensions={props.subdimensions}
          selectedOptimization={optimizationIndexOptions[selectedOptimization]}
          onChangeOptimization={setSelectedOptimization}
          columnmType={selectedColumnType}
        />
      </Stack>
    </Card>
  );
};
